
import React, { useState, useEffect } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form';
import { RegistrationFormValues } from '@/schemas/registrationSchema';
import { Coins, HeartHandshake } from 'lucide-react';

import FixedContributionDisplay from './FixedContributionDisplay';
import PresetAmountSelector from './PresetAmountSelector';
import CustomAmountButton from './CustomAmountButton';
import CustomAmountInput from './CustomAmountInput';
import ContributionFootnote from './ContributionFootnote';

interface ContributionFieldsProps {
  control: Control<RegistrationFormValues>;
}

const ContributionFields: React.FC<ContributionFieldsProps> = ({ control }) => {
  const [customAmount, setCustomAmount] = useState<boolean>(false);
  const [selectedAmount, setSelectedAmount] = useState<number>(500000);
  const [customInputValue, setCustomInputValue] = useState<number>(0);
  const [userChangedToCustom, setUserChangedToCustom] = useState<boolean>(false);
  
  // Watch the additional contribution field to sync with UI
  const contributionValue = useWatch({
    control,
    name: 'additionalContribution',
    defaultValue: 500000
  });
  
  // Effect to sync field value with component state when form is restored or updated
  useEffect(() => {
    if (typeof contributionValue !== 'undefined') {
      const currentValue = contributionValue as number;
      
      // If form has a value that doesn't match preset amounts, show custom input
      if (currentValue && ![0, 500000, 1000000, 2000000, 3000000].includes(currentValue) || userChangedToCustom) {
        setCustomAmount(true);
        setCustomInputValue(currentValue);
        setSelectedAmount(null);
      } else if (!userChangedToCustom && currentValue !== null && currentValue !== undefined) {
        setSelectedAmount(currentValue);
        setCustomAmount(false);
      }
    }
  }, [contributionValue, userChangedToCustom]);
  
  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount(false);
    setUserChangedToCustom(false);
    
    // Update the form value through the onChange handler
    control._formValues.additionalContribution = amount;
  };

  const handleCustomAmountChange = (amount: number) => {
    console.log("Custom amount changed to:", amount);
    setCustomInputValue(amount);
    
    // Update the form value directly
    control._formValues.additionalContribution = amount;
  };

  const handleCustomAmountCancel = () => {
    setCustomAmount(false);
    setUserChangedToCustom(false);
    setSelectedAmount(500000);
    
    // Update the form value
    control._formValues.additionalContribution = 500000;
  };

  return (
    <div className="space-y-8 bg-gradient-to-br from-lime-50 to-emerald-50 p-6 rounded-xl border border-lime-200 shadow-sm">
      <div className="flex items-center gap-2 border-b pb-4 border-lime-200">
        <HeartHandshake className="text-lime-600" size={28} />
        <h3 className="text-xl font-bold text-lime-800">Đóng góp của bạn</h3>
      </div>
      
      <FixedContributionDisplay />
      
      <FormField
        control={control}
        name="additionalContribution"
        render={({ field }) => (
          <FormItem className="space-y-6">
            <FormLabel className="flex items-center gap-2 text-xl font-bold text-lime-800">
              <Coins className="text-lime-600" size={24} />
              <span>Đóng góp thêm</span>
            </FormLabel>
            
            <FormControl>
              {!customAmount ? (
                <div className="space-y-6">
                  <PresetAmountSelector 
                    selectedAmount={selectedAmount} 
                    onAmountSelect={(amount) => {
                      setSelectedAmount(amount);
                      field.onChange(amount);
                    }} 
                  />
                  
                  <CustomAmountButton 
                    onClick={() => {
                      setCustomAmount(true);
                      setUserChangedToCustom(true);
                      setSelectedAmount(null);
                      // Don't reset the value when switching to custom mode
                    }}
                  />
                </div>
              ) : (
                <CustomAmountInput 
                  initialValue={customInputValue}
                  onChange={(amount) => {
                    setCustomInputValue(amount);
                    field.onChange(amount);
                  }}
                  onCancel={handleCustomAmountCancel}
                />
              )}
            </FormControl>
            
            <ContributionFootnote />
          </FormItem>
        )}
      />
    </div>
  );
};

export default ContributionFields;
